import {Box, FormHelperText, IconButton, InputAdornment, TextField as MuiTextField} from "@mui/material";
import {alpha, useTheme} from "@mui/material/styles";
import {useState, useRef} from "react";
import {FiCalendar, FiX} from "react-icons/fi";

const MAX_DIGITS = 8;
const FULL_MASK = "DD/MM/YYYY";

const formatDateDisplay = (digits) => {
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
};

const convertDDMMYYYYtoYYYYMMDD = (ddmmyyyy) => {
  if (!ddmmyyyy || ddmmyyyy.length !== 10) return "";
  const [dd, mm, yyyy] = ddmmyyyy.split("/");
  return `${yyyy}-${mm}-${dd}`;
};

const convertYYYYMMDDtoDigits = (yyyymmdd) => {
  if (!yyyymmdd || yyyymmdd.length !== 10) return "";
  const [yyyy, mm, dd] = yyyymmdd.split("-");
  return `${dd}${mm}${yyyy}`;
};

// El cursor siempre se mantiene al final, evita el reordenamiento de editar en medio de la fecha.
const moveCaretToEnd = (input) => {
  requestAnimationFrame(() => {
    if (input) {
      const end = input.value.length;
      input.setSelectionRange(end, end);
    }
  });
};

const MaskedDateInput = ({label, digits, setDigits, otherDigits, isFrom, emit, onEnter, size, inputRef, error}) => {
  const theme = useTheme();
  const [focused, setFocused] = useState(false);
  const dateInputRef = useRef(null);

  const openPicker = () => {
    try {
      dateInputRef.current?.showPicker?.();
    } catch {
      console.warn("showPicker no soportado");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      onEnter?.();
      return;
    }
    if (["Tab", "Shift", "Control", "Meta", "Alt", "CapsLock"].includes(e.key)) {
      return;
    }
    if ((e.ctrlKey || e.metaKey) && ["v", "V", "c", "C", "a", "A", "x", "X"].includes(e.key)) {
      return;
    }

    e.preventDefault();

    let newDigits = digits;
    if (e.key >= "0" && e.key <= "9") {
      if (digits.length >= MAX_DIGITS) return;
      newDigits = digits + e.key;
    } else if (e.key === "Backspace" || e.key === "Delete") {
      if (digits.length === 0) return;
      newDigits = digits.slice(0, -1);
    } else {
      return;
    }

    setDigits(newDigits);
    emit(isFrom ? newDigits : otherDigits, isFrom ? otherDigits : newDigits);
    moveCaretToEnd(inputRef.current);
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, MAX_DIGITS);
    if (!pasted) return;
    setDigits(pasted);
    emit(isFrom ? pasted : otherDigits, isFrom ? otherDigits : pasted);
    moveCaretToEnd(inputRef.current);
  };

  const handleClear = () => {
    setDigits("");
    emit(isFrom ? "" : otherDigits, isFrom ? otherDigits : "");
    inputRef.current?.focus();
  };

  const handleDatePick = (e) => {
    const isoDate = e.target.value;
    if (!isoDate) return;
    const newDigits = convertYYYYMMDDtoDigits(isoDate);
    setDigits(newDigits);
    emit(isFrom ? newDigits : otherDigits, isFrom ? otherDigits : newDigits);
  };

  const formatted = formatDateDisplay(digits);
  const remainingMask = FULL_MASK.slice(formatted.length);
  const otherFormatted = formatDateDisplay(otherDigits);
  const otherIsoValue = otherFormatted.length === 10 ? convertDDMMYYYYtoYYYYMMDD(otherFormatted) : "";
  const isoValue = formatted.length === 10 ? convertDDMMYYYYtoYYYYMMDD(formatted) : "";

  return (
    <Box sx={{position: "relative", width: 240}}>
      <MuiTextField
        inputRef={inputRef}
        label={label}
        size={size}
        value={formatted}
        onChange={() => {}}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onFocus={() => {
          setFocused(true);
          moveCaretToEnd(inputRef.current);
        }}
        onClick={() => {
          moveCaretToEnd(inputRef.current);
          openPicker();
        }}
        onBlur={() => setFocused(false)}
        error={!!error}
        InputLabelProps={{shrink: focused || !!digits}}
        sx={{
          "& .MuiInputLabel-root:not(.MuiInputLabel-shrink)": {
            maxWidth: "calc(100% - 64px)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap"
          }
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end" sx={{gap: 0.5}}>
              {digits && (
                <IconButton
                  size="small"
                  tabIndex={-1}
                  onClick={handleClear}
                  sx={{p: 0.25, color: theme.palette.text.primary}}
                >
                  <FiX size={14} />
                </IconButton>
              )}
              <Box
                sx={{
                  position: "relative",
                  display: "flex",
                  borderRadius: 1,
                  backgroundColor: theme.palette.colors.background
                }}
              >
                <IconButton
                  size="small"
                  tabIndex={-1}
                  edge="end"
                  aria-label="Elegir fecha"
                  sx={{p: 0.25, color: theme.palette.text.secondary}}
                >
                  <FiCalendar size={14} />
                </IconButton>
                <input
                  ref={dateInputRef}
                  type="date"
                  aria-label={label}
                  value={isoValue}
                  onChange={handleDatePick}
                  min={isFrom ? undefined : otherIsoValue || undefined}
                  max={isFrom ? otherIsoValue || undefined : undefined}
                  tabIndex={-1}
                  style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    opacity: 0,
                    border: "none",
                    padding: 0,
                    cursor: "pointer"
                  }}
                />
              </Box>
            </InputAdornment>
          )
        }}
        fullWidth
      />
      {focused && remainingMask && (
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: "14px",
            right: digits ? 56 : 30,
            display: "flex",
            alignItems: "center",
            overflow: "hidden",
            pointerEvents: "none",
            fontSize: "1rem",
            whiteSpace: "pre"
          }}
        >
          <span style={{visibility: "hidden"}}>{formatted}</span>
          <span style={{color: alpha(theme.palette.text.secondary, 0.7)}}>{remainingMask}</span>
        </Box>
      )}
    </Box>
  );
};

const DateRangeField = ({config, value, onChange, onEnter, size}) => {
  const [from = "", to = ""] = (value ?? "").split(",");
  const [fromDigits, setFromDigits] = useState(convertYYYYMMDDtoDigits(from));
  const [toDigits, setToDigits] = useState(convertYYYYMMDDtoDigits(to));
  const [rangeError, setRangeError] = useState(false);
  const fromRef = useRef(null);
  const toRef = useRef(null);

  const emit = (newFromDigits, newToDigits) => {
    const fromFormatted = formatDateDisplay(newFromDigits);
    const toFormatted = formatDateDisplay(newToDigits);
    const fromValue = fromFormatted.length === 10 ? convertDDMMYYYYtoYYYYMMDD(fromFormatted) : "";
    const toValue = toFormatted.length === 10 ? convertDDMMYYYYtoYYYYMMDD(toFormatted) : "";

    const isInvalidRange = !!fromValue && !!toValue && fromValue > toValue;
    setRangeError(isInvalidRange);

    if (isInvalidRange) {
      onChange("");
      return;
    }

    onChange(!fromValue && !toValue ? "" : `${fromValue},${toValue}`);
  };

  return (
    <Box>
      <Box sx={{display: "flex", gap: 0.5, alignItems: "center"}}>
        <MaskedDateInput
          label="Fecha expiración desde"
          digits={fromDigits}
          setDigits={setFromDigits}
          otherDigits={toDigits}
          isFrom
          emit={emit}
          onEnter={onEnter}
          size={size}
          inputRef={fromRef}
          error={rangeError}
        />
        <MaskedDateInput
          label="Fecha expiración hasta"
          digits={toDigits}
          setDigits={setToDigits}
          otherDigits={fromDigits}
          isFrom={false}
          emit={emit}
          onEnter={onEnter}
          size={size}
          inputRef={toRef}
          error={rangeError}
        />
      </Box>
      {rangeError && (
        <FormHelperText error sx={{ml: 0.5}}>
          La fecha "desde" no puede ser posterior a la fecha "hasta"
        </FormHelperText>
      )}
    </Box>
  );
};

export default DateRangeField;
